import { useMemo } from "react";
import { UserPersona, TeamType } from "@/types/user";
import { useUser } from "@/contexts/UserContext";
import OnboardingProgress from "./onboarding/OnboardingProgress";
import OnboardingStepCard from "./onboarding/OnboardingStepCard";
import RoleSelectionStep from "./onboarding/Steps/RoleSelectionStep";
import TeamSelectionStep from "./onboarding/Steps/TeamSelectionStep";
import OrganizationSelectionStep from "./onboarding/Steps/OrganizationSelectionStep";
import FunctionalAreaStep from "./onboarding/Steps/FunctionalAreaStep";
import SkillsSelectionStep from "./onboarding/Steps/SkillsSelectionStep";
import ProfilePictureStep from "./onboarding/Steps/ProfilePictureStep";
import CalendarIntegrationStep from "./onboarding/Steps/CalendarIntegrationStep";
import NotificationPreferencesStep from "./onboarding/Steps/NotificationPreferencesStep";
import PlanSelectionStep from "./onboarding/Steps/PlanSelectionStep";
import { useOnboardingSteps } from "@/hooks/useOnboardingSteps";

interface OnboardingProps {
  onComplete: () => void;
  // Props pour les invitations d'équipe
  isInvitedUser?: boolean;
  invitedTeamId?: string;
  invitedTeamName?: string;
  invitedTeamType?: TeamType;
}

const Onboarding = ({ 
  onComplete, 
  isInvitedUser = false,
  invitedTeamId,
  invitedTeamName,
  invitedTeamType
}: OnboardingProps) => {
  const { user, updateUserProfile } = useUser();
  const {
    currentStep,
    selectedPersona,
    selectedTeam,
    selectedTeamId,
    selectedTeamName,
    selectedOrganizationId,
    selectedOrganizationName,
    selectedSkills,
    selectedPlanId,
    hasCompletedPlanSelection,
    notificationPreferences,
    handleStepComplete,
    handleStepBack,
    setSelectedPersona,
    setSelectedTeam,
    setSelectedTeamName,
    setSelectedTeamId,
    setSelectedOrganizationId,
    setSelectedOrganizationName,
    handleSkillToggle,
    setSelectedPlanId,
    setHasCompletedPlanSelection,
    setNotificationPreferences,
  } = useOnboardingSteps(onComplete, updateUserProfile, {
    isInvitedUser,
    invitedTeamId,
    invitedTeamName,
    invitedTeamType
  });

  // Updated flow: Role → Organization (if needed) → Team/Area → Skills → Profile → Notifications → Calendar
  // Independent solo users skip organization and team steps
  // IMPORTANT: Use memoized values to prevent step recalculation during re-renders
  const needsOrganizationStep = useMemo(() => 
    !isInvitedUser && selectedPersona !== "independentSolo", 
    [isInvitedUser, selectedPersona]
  );
  const needsTeamStep = useMemo(() => 
    !isInvitedUser && 
    (selectedPersona === "newTeamMember" || selectedPersona === "manager" || selectedPersona === "organizationAdmin"),
    [isInvitedUser, selectedPersona]
  );
  
  // For invited users: they skip role/org/team selection and go directly to skills → profile → notifications → calendar (4 steps)
  // For regular users: depends on their persona and flow
  const totalSteps = isInvitedUser ? 4 : (
    1 + // Role selection
    (needsOrganizationStep ? 1 : 0) + // Organization selection
    1 + // Team/Functional area selection
    5   // Skills, Profile, Plan Selection, Notifications, Calendar
  );
  
  // Calculate step numbers based on flow - memoized to prevent recalculation
  const getStepNumber = useMemo(() => (stepType: string) => {
    // For invited users, they skip to the final steps (no plan selection for invited users)
    if (isInvitedUser) {
      if (stepType === "skills") return 1;
      if (stepType === "profile") return 2;
      if (stepType === "notifications") return 3;
      if (stepType === "calendar") return 4;
      return 1; // Default to skills step for invited users
    }
    
    // For regular users
    let step = 1; // Role selection
    
    if (stepType === "role") return 1;
    
    step++; // Move past role selection (now at 2)
    
    if (needsOrganizationStep) {
      if (stepType === "organization") return step;
      step++; // Move past organization selection
    }
    
    if (stepType === "team") return step;
    step++; // Move past team selection
    
    if (stepType === "skills") return step;
    step++; // Move past skills
    
    if (stepType === "profile") return step;
    step++; // Move past profile
    
    if (!isInvitedUser) {
      if (stepType === "plan") return step;
      step++; // Move past plan selection
    }
    
    if (stepType === "notifications") return step;
    step++; // Move past notifications
    
    if (stepType === "calendar") return step;
    
    return step;
  }, [isInvitedUser, needsOrganizationStep]);
  
  
  // For invited users, currentStep maps directly to their simplified flow
  const adjustedCurrentStep = currentStep;

  return (
    <div className="max-w-5xl mx-auto py-6 px-4 md:py-12">
      <div className="text-center mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">
          {currentStep > 1 ? "Welcome back!" : "Welcome to Step"}
        </h1>
        <p className="text-sm md:text-base text-muted-foreground px-2">
          {currentStep > 1 
            ? "Let's continue where you left off"
            : isInvitedUser 
            ? `You've been invited to join ${invitedTeamName}. Let's set up your profile.`
            : "Let's set up your profile to get the most out of your experience"
          }
        </p>
      </div>

      <OnboardingProgress currentStep={adjustedCurrentStep} totalSteps={totalSteps} />

      {currentStep === 1 && (
        <OnboardingStepCard
          title="What best describes your role?"
          description="This helps us personalize your experience"
          onContinue={handleStepComplete}
        >
          <RoleSelectionStep
            selectedPersona={selectedPersona}
            setSelectedPersona={setSelectedPersona}
          />
        </OnboardingStepCard>
      )}

      {/* Organization Selection (for non-independent users) */}
      {currentStep === getStepNumber("organization") && !isInvitedUser && needsOrganizationStep && (
        <OnboardingStepCard
          title="Choose or Create Organization"
          description="Organizations help you collaborate with colleagues and track team progress"
          onBack={handleStepBack}
          onContinue={handleStepComplete}
        >
          <OrganizationSelectionStep
            selectedOrganizationId={selectedOrganizationId}
            setSelectedOrganizationId={setSelectedOrganizationId}
            selectedOrganizationName={selectedOrganizationName}
            setSelectedOrganizationName={setSelectedOrganizationName}
            userEmail={user?.email || ""}
            currentUserId={user?.id}
          />
        </OnboardingStepCard>
      )}

      {/* Team/Functional Area Selection */}
      {currentStep === getStepNumber("team") && !isInvitedUser && (
        <>
          {/* Solo users (independent or organizational) see functional area selection */}
          {(selectedPersona === "independentSolo" || selectedPersona === "organizationalSolo") && (
            <OnboardingStepCard
              title="What's your functional area?"
              description="This helps us provide relevant skills and insights for your role"
              onBack={handleStepBack}
              onContinue={handleStepComplete}
            >
              <FunctionalAreaStep
                selectedArea={selectedTeam}
                setSelectedArea={setSelectedTeam}
              />
            </OnboardingStepCard>
          )}
          
          {/* Team users see team selection */}
          {needsTeamStep && (
            <OnboardingStepCard
              title="Which team are you part of?"
              description="We will customize your dashboard based on your team"
              onBack={handleStepBack}
              onContinue={handleStepComplete}
            >
              <TeamSelectionStep
                selectedTeam={selectedTeam}
                setSelectedTeam={setSelectedTeam}
                selectedTeamId={selectedTeamId}
                setSelectedTeamId={setSelectedTeamId}
                selectedTeamName={selectedTeamName}
                setSelectedTeamName={setSelectedTeamName}
                organizationId={selectedOrganizationId}
              />
            </OnboardingStepCard>
          )}
        </>
      )}

      {/* Skills Selection */}
      {currentStep === getStepNumber("skills") && (
        <OnboardingStepCard
          title="What skills would you like to develop?"
          description="Select skills you want to track, develop, or share with your team"
          onBack={handleStepBack}
          onContinue={handleStepComplete}
        >
          <SkillsSelectionStep
            selectedSkills={selectedSkills}
            handleSkillToggle={handleSkillToggle}
          />
        </OnboardingStepCard>
      )}

      {/* Profile Picture */}
      {currentStep === getStepNumber("profile") && (
        <OnboardingStepCard
          title="Add your profile picture"
          description="Help your team recognize you with a profile photo"
          onBack={handleStepBack}
          onContinue={handleStepComplete}
          hideButtons={true}
        >
          <ProfilePictureStep onSkip={handleStepComplete} onBack={handleStepBack} />
        </OnboardingStepCard>
      )}

      {/* Plan Selection - Only for regular users, not invited users */}
      {!isInvitedUser && currentStep === getStepNumber("plan") && (
        <OnboardingStepCard
          title="Choose your plan"
          description="Select the plan that best fits your needs. Have a promo code? Enter it during checkout."
          onBack={handleStepBack}
          onContinue={handleStepComplete}
          hideButtons={true}
        >
          <PlanSelectionStep 
            onPlanSelected={(planId) => {
              setSelectedPlanId(planId);
              setHasCompletedPlanSelection(true);
            }}
            onComplete={handleStepComplete} 
            onBack={handleStepBack} 
          />
        </OnboardingStepCard>
      )}

      {/* Notification Preferences */}
      {currentStep === getStepNumber("notifications") && (
        <OnboardingStepCard
          title="Notification preferences"
          description="Choose how you want to stay updated with Step"
          onBack={handleStepBack}
          onContinue={handleStepComplete}
        >
          <NotificationPreferencesStep
            preferences={notificationPreferences}
            setPreferences={setNotificationPreferences}
          />
        </OnboardingStepCard>
      )}

      {/* Calendar Integration (final step) */}
      {currentStep === getStepNumber("calendar") && (
        <OnboardingStepCard
          title="Connect your calendar"
          description="Sync your calendar to get better scheduling insights and integration with check-ins"
          onBack={handleStepBack}
          onContinue={handleStepComplete}
          isLastStep
          hideButtons={true}
        >
          <CalendarIntegrationStep onSkip={handleStepComplete} />
        </OnboardingStepCard>
      )}
    </div>
  );
};

export default Onboarding;
